"use client";

import { useRef, useState, type ChangeEvent } from "react";
import { Box, IconButton, Link, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from "@/ui";
import TextField from "@components/shared/form/TextField";
import usePortfolioUpload from "@lib/candidates/usePortfolioUpload";
import usePortfolioUsage, { type PortfolioItemUsage } from "@lib/candidates/usePortfolioUsage";
import useUpdatePortfolio from "@lib/candidates/useUpdatePortfolio";
import type { PortfolioItem } from "@lib/candidates/saveItemToProfile";
import {
  cloudinaryTransform,
  cloudinaryVideoPoster,
  PORTFOLIO_EDIT_THUMB_TRANSFORM,
  PORTFOLIO_VIDEO_POSTER_TRANSFORM,
} from "@lib/cloudinary";
import toast from "react-hot-toast";

const IMAGE_MAX_BYTES = 1024 * 1024;
const VIDEO_MAX_BYTES = 100 * 1024 * 1024;

type EditablePortfolioItem = PortfolioItem & { is_visible?: boolean };

interface EditPortfolioProps {
  open: boolean;
  onClose: () => void;
  candidate: { uuid: string; portfolio?: EditablePortfolioItem[] | null };
}

/** Files and videos are the only items whose copies are removed from floats and submissions. */
const hasStoredFile = (item: EditablePortfolioItem) => item.type === "file" || item.type === "video";

const countOf = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

export const describeUsage = ({ floats, submissions }: PortfolioItemUsage) =>
  [floats > 0 && countOf(floats, "float"), submissions > 0 && countOf(submissions, "submission")]
    .filter(Boolean)
    .join(" and ");

export default function EditPortfolio({ onClose, open, candidate }: EditPortfolioProps) {
  const [items, setItems] = useState<EditablePortfolioItem[]>(candidate?.portfolio || []);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [pendingRemoval, setPendingRemoval] = useState<EditablePortfolioItem | null>(null);
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  // The dialog stays mounted while closed, so every open starts again from the
  // saved portfolio: a Cancel, or a save that failed, leaves nothing pending.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setItems(candidate?.portfolio || []);
      setRemovedIds([]);
      setPendingRemoval(null);
    }
  }
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const {
    upload,
    uploadImage,
    uploadVideo,
    isUploading,
    isUploadingFile,
    isUploadingImage,
    isUploadingVideo,
  } = usePortfolioUpload();
  // Fetched again on every open; until it's back, stored files can't be
  // removed, so the warning never runs on an earlier answer.
  const { data: usage, isFetching: isCheckingUsage, isError: usageFailed } = usePortfolioUsage(candidate?.uuid, open);
  const updatePortfolio = useUpdatePortfolio();
  const saving = updatePortfolio.isPending;
  const handleClose = () => {
    if (!saving) onClose();
  };

  const pendingUsage = pendingRemoval ? usage?.[pendingRemoval.id] : undefined;

  const handleTitleChange = (id: string, value: string) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, title: value } : item)));
  };

  const handleUrlChange = (id: string, value: string) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, url: value } : item)));
  };

  const removeItem = (id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
    setRemovedIds((current) => [...current, id]);
  };

  const handleRemove = (item: EditablePortfolioItem) => {
    if (usage?.[item.id] || (usageFailed && hasStoredFile(item))) {
      setPendingRemoval(item);
      return;
    }
    removeItem(item.id);
  };

  const handleConfirmRemoval = () => {
    if (pendingRemoval) removeItem(pendingRemoval.id);
    setPendingRemoval(null);
  };

  const handleAddLink = () => {
    setItems((current) => [...current, { id: crypto.randomUUID(), title: "", url: "", type: "link" }]);
  };

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = await upload(candidate.uuid, file);
      setItems((current) => [...current, { id: crypto.randomUUID(), title: result.original_filename, url: result.path, type: "file", original_filename: result.original_filename }]);
    } catch (err) {
      console.error("Portfolio upload failed:", err);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 1 MB or smaller.");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    try {
      const result = await uploadImage(candidate.uuid, file);
      setItems((current) => [...current, { id: crypto.randomUUID(), title: result.original_filename, url: result.url, type: "image", original_filename: result.original_filename, public_id: result.public_id }]);
    } catch (err) {
      console.error("Portfolio image upload failed:", err);
      toast.error("Image upload failed. Check size and format.");
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleVideoSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > VIDEO_MAX_BYTES) {
      toast.error("Video must be 100 MB or smaller.");
      if (videoInputRef.current) videoInputRef.current.value = "";
      return;
    }
    setVideoProgress(0);
    try {
      const result = await uploadVideo(candidate.uuid, file, setVideoProgress);
      setItems((current) => [...current, { id: crypto.randomUUID(), title: result.original_filename, url: result.url, type: "video", original_filename: result.original_filename, public_id: result.public_id }]);
    } catch (err) {
      console.error("Portfolio video upload failed:", err);
      toast.error("Video upload failed. Check size and format.");
    } finally {
      setVideoProgress(null);
    }
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  // The dialog stays open until the save is done: a failed save keeps every
  // edit here to try again, and reopening never shows the list from before it.
  const handleSubmit = () => {
    const validItems = items.filter((item) => item.title && item.url);
    updatePortfolio.mutate(
      { uuid: candidate.uuid, portfolio: validItems, removed_ids: removedIds },
      {
        onSuccess: () => onClose(),
        onError: () => {
          toast.error("Failed to save");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit portfolio</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add links to external portfolios or upload files to share with contacts.
            </Typography>
            {items.map((item) => (
              <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: "4px" }}>
                {item.type === "image" ? (
                  <Box component="img" src={cloudinaryTransform(item.url, PORTFOLIO_EDIT_THUMB_TRANSFORM)} alt={item.title || "Portfolio image"} sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 1, flexShrink: 0 }} />
                ) : item.type === "video" ? (
                  <Box sx={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
                    <Box component="img" src={cloudinaryVideoPoster(item.url, PORTFOLIO_VIDEO_POSTER_TRANSFORM)} alt={item.title || "Portfolio video"} sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 1 }} />
                    <PlayCircleOutlineIcon sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "white", fontSize: 24, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }} />
                  </Box>
                ) : item.type === "file" ? (
                  <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                ) : (
                  <LinkIcon fontSize="small" color="action" />
                )}
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField fullWidth variant="outlined" placeholder="Title" size="small" value={item.title} onChange={(e) => handleTitleChange(item.id, e.target.value)} />
                  {item.type === "link" ? (
                    <TextField fullWidth variant="outlined" placeholder="URL" size="small" value={item.url} onChange={(e) => handleUrlChange(item.id, e.target.value)} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {item.type === "image" ? item.original_filename : item.original_filename || item.url}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Remove ${item.title || "item"}`}
                  disabled={isCheckingUsage && hasStoredFile(item)}
                  onClick={() => handleRemove(item)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 2, flexWrap: "wrap" }}>
              <Button variant="secondary" size="sm" onClick={handleAddLink}>Add link</Button>
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                {isUploadingFile ? "Uploading..." : "Upload file"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => imageInputRef.current?.click()} disabled={isUploading}>
                {isUploadingImage ? "Uploading..." : "Upload image"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => videoInputRef.current?.click()} disabled={isUploading}>
                {isUploadingVideo
                  ? `Uploading${videoProgress != null ? ` ${videoProgress}%` : "..."}`
                  : "Upload video"}
              </Button>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.ppt,.pptx,.xls,.xlsx" style={{ display: "none" }} />
              <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept=".jpg,.jpeg,.png,.gif,.webp" style={{ display: "none" }} />
              <input type="file" ref={videoInputRef} onChange={handleVideoSelect} accept="video/*" style={{ display: "none" }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Files up to 10 MB, images up to 1 MB, videos up to 100 MB. Reduce image sizes with{" "}
              <Link href="https://tinypng.com/" target="_blank" rel="noopener noreferrer">TinyPng</Link>.
            </Typography>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} loading={saving}>Save changes</Button>
          </DialogFooter>

          <Dialog open={!!pendingRemoval} onOpenChange={(o) => { if (!o) setPendingRemoval(null); }}>
            <DialogPortal>
              <DialogBackdrop />
              <DialogContent size="sm">
                <DialogHeader>
                  <DialogTitle>
                    {pendingUsage ? `Also remove from ${describeUsage(pendingUsage)}?` : "Also remove from floats and submissions?"}
                  </DialogTitle>
                  <DialogClose aria-label="Close" />
                </DialogHeader>
                <DialogBody>
                  <DialogDescription>
                    {pendingUsage
                      ? `“${pendingRemoval?.title}” is also in ${describeUsage(pendingUsage)}. Saving will delete the file and remove it from those too, so recipients will no longer see it.`
                      : `We couldn't check whether “${pendingRemoval?.title}” is in any floats or submissions. Saving will delete the file and remove it from any that use it.`}{" "}
                    This cannot be undone.
                  </DialogDescription>
                </DialogBody>
                <DialogFooter>
                  <Button variant="secondary" onClick={() => setPendingRemoval(null)}>
                    Keep it
                  </Button>
                  <Button variant="destructive" onClick={handleConfirmRemoval}>
                    Remove everywhere
                  </Button>
                </DialogFooter>
              </DialogContent>
            </DialogPortal>
          </Dialog>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
